import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Baby, Plus, Calendar, Syringe, CheckCircle2, AlertCircle, Clock, 
  ChevronRight, Trash2, Edit, Bell, Mail, Shield, Activity,
  User, FileText, Download, ChevronDown, ChevronUp, X
} from 'lucide-react';
import Navigation from '../components/Navigation';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { API } from '../App';

const VaccinationTracker = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Dialog states
  const [showAddChild, setShowAddChild] = useState(false);
  const [showAddRecord, setShowAddRecord] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [editingChild, setEditingChild] = useState(null);
  
  // Form states
  const [childForm, setChildForm] = useState({
    name: '',
    date_of_birth: '',
    gender: 'male',
    blood_group: '',
    allergies: '',
    notes: ''
  });
  
  const [recordForm, setRecordForm] = useState({
    vaccine_name: '',
    dose: '',
    administered_date: '',
    administered_by: '',
    hospital_clinic: '',
    batch_number: '',
    notes: ''
  });
  
  const [reminderSettings, setReminderSettings] = useState({
    email_reminders: true,
    reminder_days_before: 7,
    email: ''
  });
  
  const [expandedGroups, setExpandedGroups] = useState({});

  const fetchChildren = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/vaccination/children`);
      setChildren(response.data);
      if (response.data.length > 0) {
        setSelectedChild(prev => prev || response.data[0]);
      }
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load children');
      setLoading(false);
    }
  }, []);

  const fetchChildData = useCallback(async (childId) => {
    try {
      const [dashboardRes, scheduleRes, recordsRes] = await Promise.all([
        axios.get(`${API}/vaccination/dashboard/${childId}`),
        axios.get(`${API}/vaccination/schedule/${childId}`),
        axios.get(`${API}/vaccination/records/${childId}`)
      ]);
      setDashboard(dashboardRes.data);
      setSchedule(scheduleRes.data);
      setRecords(recordsRes.data);
    } catch (error) {
      toast.error('Failed to load vaccination data');
    }
  }, []);

  const fetchReminderSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/vaccination/reminders/settings`);
      setReminderSettings(response.data);
    } catch (error) {
      toast.error('Failed to load reminder settings');
    }
  }, []);

  useEffect(() => {
    fetchChildren();
    fetchReminderSettings();
  }, [fetchChildren, fetchReminderSettings]);

  useEffect(() => {
    if (selectedChild) {
      fetchChildData(selectedChild.id);
    }
  }, [selectedChild, fetchChildData]);

  const handleAddChild = async () => {
    try {
      const data = {
        ...childForm,
        allergies: childForm.allergies ? childForm.allergies.split(',').map(a => a.trim()) : []
      };
      
      if (editingChild) {
        await axios.put(`${API}/vaccination/children/${editingChild.id}`, data);
        toast.success('Child profile updated successfully');
      } else {
        await axios.post(`${API}/vaccination/children`, data);
        toast.success('Child added successfully');
      }
      
      setShowAddChild(false);
      setEditingChild(null);
      setChildForm({ name: '', date_of_birth: '', gender: 'male', blood_group: '', allergies: '', notes: '' });
      fetchChildren();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error saving child');
    }
  };

  const handleDeleteChild = async (childId) => {
    if (!window.confirm('Are you sure you want to delete this child and all vaccination records?')) return;
    
    try {
      await axios.delete(`${API}/vaccination/children/${childId}`);
      toast.success('Child deleted successfully');
      if (selectedChild?.id === childId) {
        setSelectedChild(null);
      }
      fetchChildren();
    } catch (error) {
      toast.error('Error deleting child');
    }
  };

  const handleAddRecord = async () => {
    try {
      const data = {
        ...recordForm,
        child_id: selectedChild.id
      };
      
      await axios.post(`${API}/vaccination/records`, data);
      toast.success('Vaccination record added successfully');
      setShowAddRecord(false);
      setRecordForm({ vaccine_name: '', dose: '', administered_date: '', administered_by: '', hospital_clinic: '', batch_number: '', notes: '' });
      fetchChildData(selectedChild.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error adding record');
    }
  };

  const handleDeleteRecord = async (recordId) => {
    if (!window.confirm('Are you sure you want to delete this vaccination record?')) return;
    
    try {
      await axios.delete(`${API}/vaccination/records/${recordId}`);
      toast.success('Record deleted successfully');
      fetchChildData(selectedChild.id);
    } catch (error) {
      toast.error('Error deleting record');
    }
  };

  const handleSaveSettings = async () => {
    try {
      await axios.put(`${API}/vaccination/reminders/settings`, reminderSettings);
      toast.success('Reminder settings saved');
      setShowSettings(false);
    } catch (error) {
      toast.error('Error saving settings');
    }
  };

  const handleMarkAsGiven = (vaccine) => {
    setRecordForm({
      vaccine_name: vaccine.vaccine,
      dose: vaccine.dose,
      administered_date: new Date().toISOString().split('T')[0],
      administered_by: '',
      hospital_clinic: '',
      batch_number: '',
      notes: ''
    });
    setShowAddRecord(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-700 border-green-200';
      case 'pending': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'overdue': return 'bg-red-100 text-red-700 border-red-200';
      case 'upcoming': return 'bg-blue-100 text-blue-700 border-blue-200';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle2 className="w-4 h-4 text-green-600" />;
      case 'pending': return <Clock className="w-4 h-4 text-yellow-600" />;
      case 'overdue': return <AlertCircle className="w-4 h-4 text-red-600" />;
      case 'upcoming': return <Calendar className="w-4 h-4 text-blue-600" />;
      default: return null;
    }
  };

  // Group schedule by age
  const groupedSchedule = schedule.reduce((acc, vaccine) => {
    const key = vaccine.age_label;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(vaccine);
    return acc;
  }, {});

  const toggleGroup = (group) => {
    setExpandedGroups(prev => ({
      ...prev,
      [group]: !prev[group]
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation user={user} setUser={setUser} />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50">
      <Navigation user={user} setUser={setUser} />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent flex items-center gap-3">
              <Baby className="w-8 h-8 text-teal-600" />
              Child Vaccination Tracker
            </h1>
            <p className="text-gray-600 mt-1">Track your child's immunization schedule (IAP Guidelines)</p>
          </div>
          
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setShowSettings(true)}
              className="gap-2"
            >
              <Bell className="w-4 h-4" />
              Reminders
            </Button>
            <Button
              onClick={() => {
                setEditingChild(null);
                setChildForm({ name: '', date_of_birth: '', gender: 'male', blood_group: '', allergies: '', notes: '' });
                setShowAddChild(true);
              }}
              className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Child
            </Button>
          </div>
        </div>

        {children.length === 0 ? (
          <Card className="text-center py-16">
            <CardContent>
              <Baby className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No Children Added</h3>
              <p className="text-gray-500 mb-6">Add your child's profile to start tracking vaccinations</p>
              <Button
                onClick={() => setShowAddChild(true)}
                className="bg-gradient-to-r from-teal-600 to-cyan-600"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Child
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid lg:grid-cols-4 gap-6">
            {/* Children List Sidebar */}
            <div className="lg:col-span-1">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Your Children</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {children.map((child) => (
                    <div
                      key={child.id}
                      className={`p-3 rounded-lg cursor-pointer transition-all ${
                        selectedChild?.id === child.id
                          ? 'bg-teal-50 border-2 border-teal-200'
                          : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                      }`}
                      onClick={() => setSelectedChild(child)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            child.gender === 'male' ? 'bg-blue-100' : 'bg-pink-100'
                          }`}>
                            <Baby className={`w-5 h-5 ${
                              child.gender === 'male' ? 'text-blue-600' : 'text-pink-600'
                            }`} />
                          </div>
                          <div>
                            <p className="font-medium text-gray-800">{child.name}</p>
                            <p className="text-xs text-gray-500">{child.age_display}</p>
                          </div>
                        </div>
                        <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${
                          selectedChild?.id === child.id ? 'rotate-90' : ''
                        }`} />
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3 space-y-6">
              {selectedChild && dashboard && (
                <>
                  {/* Child Info Bar */}
                  <Card className="bg-gradient-to-r from-teal-500 to-cyan-500 text-white">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center">
                            <Baby className="w-8 h-8" />
                          </div>
                          <div>
                            <h2 className="text-xl font-bold">{selectedChild.name}</h2>
                            <p className="text-teal-100">{selectedChild.age_display} • Born: {new Date(selectedChild.date_of_birth).toLocaleDateString()}</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-white hover:bg-white/20"
                            onClick={() => {
                              setEditingChild(selectedChild);
                              setChildForm({
                                name: selectedChild.name,
                                date_of_birth: selectedChild.date_of_birth,
                                gender: selectedChild.gender,
                                blood_group: selectedChild.blood_group || '',
                                allergies: selectedChild.allergies?.join(', ') || '',
                                notes: selectedChild.notes || ''
                              });
                              setShowAddChild(true);
                            }}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-white hover:bg-red-500/50"
                            onClick={() => handleDeleteChild(selectedChild.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Stats Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card className="bg-green-50 border-green-100">
                      <CardContent className="p-4 text-center">
                        <CheckCircle2 className="w-8 h-8 text-green-600 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-green-700">{dashboard.statistics.completed_standard}</p>
                        <p className="text-xs text-green-600">Completed</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-yellow-50 border-yellow-100">
                      <CardContent className="p-4 text-center">
                        <Clock className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-yellow-700">{dashboard.statistics.pending}</p>
                        <p className="text-xs text-yellow-600">Pending</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-red-50 border-red-100">
                      <CardContent className="p-4 text-center">
                        <AlertCircle className="w-8 h-8 text-red-600 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-red-700">{dashboard.statistics.overdue}</p>
                        <p className="text-xs text-red-600">Overdue</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-teal-50 border-teal-100">
                      <CardContent className="p-4 text-center">
                        <Activity className="w-8 h-8 text-teal-600 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-teal-700">{dashboard.statistics.completion_percentage}%</p>
                        <p className="text-xs text-teal-600">Complete</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Progress Bar */}
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">Vaccination Progress</span>
                        <span className="text-sm text-gray-500">
                          {dashboard.statistics.completed_standard} of {dashboard.statistics.total_standard} standard vaccines
                        </span>
                      </div>
                      <Progress value={dashboard.statistics.completion_percentage} className="h-3" />
                    </CardContent>
                  </Card>

                  {/* Tabs */}
                  <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="grid grid-cols-3 gap-2">
                      <TabsTrigger value="dashboard" className="gap-2">
                        <Activity className="w-4 h-4" /> Overview
                      </TabsTrigger>
                      <TabsTrigger value="schedule" className="gap-2">
                        <Calendar className="w-4 h-4" /> Schedule
                      </TabsTrigger>
                      <TabsTrigger value="records" className="gap-2">
                        <FileText className="w-4 h-4" /> Records
                      </TabsTrigger>
                    </TabsList>

                    <TabsContent value="dashboard" className="space-y-4 mt-4">
                      <div className="grid md:grid-cols-2 gap-4">
                        {/* Upcoming Vaccines */}
                        <Card>
                          <CardHeader className="pb-3">
                            <CardTitle className="text-lg flex items-center gap-2">
                              <Calendar className="w-5 h-5 text-blue-600" />
                              Upcoming Vaccines
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            {dashboard.upcoming_vaccines.length === 0 ? (
                              <p className="text-gray-500 text-sm">No upcoming vaccines</p>
                            ) : (
                              <div className="space-y-3">
                                {dashboard.upcoming_vaccines.map((vaccine) => (
                                  <div key={`${vaccine.vaccine}-${vaccine.dose}-${vaccine.due_date}`} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                                    <div>
                                      <p className="font-medium text-gray-800">{vaccine.vaccine}</p>
                                      <p className="text-xs text-gray-500">{vaccine.dose} • {vaccine.age_label}</p>
                                    </div>
                                    <Badge variant="outline" className="bg-blue-100 text-blue-700">
                                      {new Date(vaccine.due_date).toLocaleDateString()}
                                    </Badge>
                                  </div>
                                ))}
                              </div>
                            )}
                          </CardContent>
                        </Card>

                        {/* Overdue Vaccines */}
                        <Card>
                          <CardHeader className="pb-3">
                            <CardTitle className="text-lg flex items-center gap-2">
                              <AlertCircle className="w-5 h-5 text-red-600" />
                              Overdue Vaccines
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            {dashboard.overdue_vaccines.length === 0 ? (
                              <p className="text-green-600 text-sm flex items-center gap-2">
                                <CheckCircle2 className="w-4 h-4" />
                                All vaccinations are up to date!
                              </p>
                            ) : (
                              <div className="space-y-3">
                                {dashboard.overdue_vaccines.map((vaccine) => (
                                  <div key={`${vaccine.vaccine}-${vaccine.dose}-overdue`} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                                    <div>
                                      <p className="font-medium text-gray-800">{vaccine.vaccine}</p>
                                      <p className="text-xs text-red-600">{vaccine.days_overdue} days overdue</p>
                                    </div>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="text-red-600 border-red-200 hover:bg-red-100"
                                      onClick={() => handleMarkAsGiven(vaccine)}
                                    >
                                      Mark Given
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      </div>
                    </TabsContent>

                    <TabsContent value="schedule" className="mt-4">
                      <Card>
                        <CardHeader className="flex flex-row items-center justify-between">
                          <div>
                            <CardTitle>IAP Vaccination Schedule</CardTitle>
                            <CardDescription>Indian Academy of Pediatrics recommended vaccines</CardDescription>
                          </div>
                          <Button
                            onClick={() => setShowAddRecord(true)}
                            size="sm"
                            className="bg-teal-600 hover:bg-teal-700"
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Add Record
                          </Button>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-4">
                            {Object.entries(groupedSchedule).map(([ageGroup, vaccines]) => {
                              const isExpanded = expandedGroups[ageGroup] !== false;
                              const completedCount = vaccines.filter(v => v.status === 'completed').length;
                              const hasOverdue = vaccines.some(v => v.status === 'overdue');
                              
                              return (
                                <div key={ageGroup} className="border rounded-lg overflow-hidden">
                                  <div
                                    className={`p-4 cursor-pointer flex items-center justify-between ${
                                      hasOverdue ? 'bg-red-50' : completedCount === vaccines.length ? 'bg-green-50' : 'bg-gray-50'
                                    }`}
                                    onClick={() => toggleGroup(ageGroup)}
                                  >
                                    <div className="flex items-center gap-3">
                                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                        completedCount === vaccines.length ? 'bg-green-100' : hasOverdue ? 'bg-red-100' : 'bg-gray-200'
                                      }`}>
                                        {completedCount === vaccines.length ? (
                                          <CheckCircle2 className="w-5 h-5 text-green-600" />
                                        ) : hasOverdue ? (
                                          <AlertCircle className="w-5 h-5 text-red-600" />
                                        ) : (
                                          <Calendar className="w-5 h-5 text-gray-600" />
                                        )}
                                      </div>
                                      <div>
                                        <p className="font-semibold text-gray-800">{ageGroup}</p>
                                        <p className="text-xs text-gray-500">{completedCount}/{vaccines.length} completed</p>
                                      </div>
                                    </div>
                                    {isExpanded ? (
                                      <ChevronUp className="w-5 h-5 text-gray-400" />
                                    ) : (
                                      <ChevronDown className="w-5 h-5 text-gray-400" />
                                    )}
                                  </div>
                                  
                                  {isExpanded && (
                                    <div className="divide-y">
                                      {vaccines.map((vaccine, idx) => (
                                        <div key={`item-${idx}`} className="p-4 flex items-center justify-between hover:bg-gray-50">
                                          <div className="flex items-center gap-3">
                                            {getStatusIcon(vaccine.status)}
                                            <div>
                                              <p className="font-medium text-gray-800">
                                                {vaccine.vaccine}
                                                {vaccine.category === 'optional' && (
                                                  <Badge variant="outline" className="ml-2 text-xs">Optional</Badge>
                                                )}
                                              </p>
                                              <p className="text-xs text-gray-500">{vaccine.dose} • {vaccine.description}</p>
                                            </div>
                                          </div>
                                          <div className="flex items-center gap-3">
                                            <Badge className={getStatusColor(vaccine.status)}>
                                              {vaccine.status}
                                            </Badge>
                                            {vaccine.status !== 'completed' && (
                                              <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => handleMarkAsGiven(vaccine)}
                                              >
                                                <Syringe className="w-4 h-4 mr-1" />
                                                Mark Given
                                              </Button>
                                            )}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </CardContent>
                      </Card>
                    </TabsContent>

                    <TabsContent value="records" className="mt-4">
                      <Card>
                        <CardHeader className="flex flex-row items-center justify-between">
                          <CardTitle>Vaccination Records</CardTitle>
                          <Button
                            onClick={() => setShowAddRecord(true)}
                            size="sm"
                            className="bg-teal-600 hover:bg-teal-700"
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Add Record
                          </Button>
                        </CardHeader>
                        <CardContent>
                          {records.length === 0 ? (
                            <div className="text-center py-8">
                              <Syringe className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                              <p className="text-gray-500">No vaccination records yet</p>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              {records.map((record) => (
                                <div key={record.id} className="p-4 border rounded-lg hover:bg-gray-50">
                                  <div className="flex items-start justify-between">
                                    <div className="flex items-start gap-3">
                                      <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                                      </div>
                                      <div>
                                        <p className="font-semibold text-gray-800">{record.vaccine_name}</p>
                                        <p className="text-sm text-gray-600">{record.dose}</p>
                                        <div className="flex flex-wrap gap-2 mt-2 text-xs text-gray-500">
                                          <span className="flex items-center gap-1">
                                            <Calendar className="w-3 h-3" />
                                            {new Date(record.administered_date).toLocaleDateString()}
                                          </span>
                                          {record.administered_by && (
                                            <span className="flex items-center gap-1">
                                              <User className="w-3 h-3" />
                                              {record.administered_by}
                                            </span>
                                          )}
                                          {record.hospital_clinic && (
                                            <span className="flex items-center gap-1">
                                              <Shield className="w-3 h-3" />
                                              {record.hospital_clinic}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                      onClick={() => handleDeleteRecord(record.id)}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </TabsContent>
                  </Tabs>
                </>
              )}
            </div>
          </div>
        )}

        {/* Add/Edit Child Dialog */}
        <Dialog open={showAddChild} onOpenChange={setShowAddChild}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{editingChild ? 'Edit Child Profile' : 'Add New Child'}</DialogTitle>
              <DialogDescription>Enter your child's information to track their vaccinations</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Child's Name *</Label>
                <Input
                  value={childForm.name}
                  onChange={(e) => setChildForm({ ...childForm, name: e.target.value })}
                  placeholder="Enter name"
                />
              </div>
              <div>
                <Label>Date of Birth *</Label>
                <Input
                  type="date"
                  value={childForm.date_of_birth}
                  onChange={(e) => setChildForm({ ...childForm, date_of_birth: e.target.value })}
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div>
                <Label>Gender *</Label>
                <Select value={childForm.gender} onValueChange={(v) => setChildForm({ ...childForm, gender: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Blood Group</Label>
                <Select value={childForm.blood_group} onValueChange={(v) => setChildForm({ ...childForm, blood_group: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select blood group" />
                  </SelectTrigger>
                  <SelectContent>
                    {['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map(bg => (
                      <SelectItem key={bg} value={bg}>{bg}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Allergies (comma separated)</Label>
                <Input
                  value={childForm.allergies}
                  onChange={(e) => setChildForm({ ...childForm, allergies: e.target.value })}
                  placeholder="e.g., Eggs, Penicillin"
                />
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={childForm.notes}
                  onChange={(e) => setChildForm({ ...childForm, notes: e.target.value })}
                  placeholder="Any additional notes"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddChild(false)}>Cancel</Button>
              <Button onClick={handleAddChild} className="bg-teal-600 hover:bg-teal-700">
                {editingChild ? 'Update' : 'Add Child'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Vaccination Record Dialog */}
        <Dialog open={showAddRecord} onOpenChange={setShowAddRecord}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Add Vaccination Record</DialogTitle>
              <DialogDescription>Record a completed vaccination for {selectedChild?.name}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Vaccine Name *</Label>
                <Input
                  value={recordForm.vaccine_name}
                  onChange={(e) => setRecordForm({ ...recordForm, vaccine_name: e.target.value })}
                  placeholder="e.g., BCG, OPV, DPT"
                />
              </div>
              <div>
                <Label>Dose *</Label>
                <Input
                  value={recordForm.dose}
                  onChange={(e) => setRecordForm({ ...recordForm, dose: e.target.value })}
                  placeholder="e.g., 1st Dose, Booster"
                />
              </div>
              <div>
                <Label>Date Administered *</Label>
                <Input
                  type="date"
                  value={recordForm.administered_date}
                  onChange={(e) => setRecordForm({ ...recordForm, administered_date: e.target.value })}
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div>
                <Label>Administered By</Label>
                <Input
                  value={recordForm.administered_by}
                  onChange={(e) => setRecordForm({ ...recordForm, administered_by: e.target.value })}
                  placeholder="Doctor's name"
                />
              </div>
              <div>
                <Label>Hospital/Clinic</Label>
                <Input
                  value={recordForm.hospital_clinic}
                  onChange={(e) => setRecordForm({ ...recordForm, hospital_clinic: e.target.value })}
                  placeholder="Clinic or hospital name"
                />
              </div>
              <div>
                <Label>Batch Number</Label>
                <Input
                  value={recordForm.batch_number}
                  onChange={(e) => setRecordForm({ ...recordForm, batch_number: e.target.value })}
                  placeholder="Vaccine batch number"
                />
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={recordForm.notes}
                  onChange={(e) => setRecordForm({ ...recordForm, notes: e.target.value })}
                  placeholder="Any reactions or notes"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddRecord(false)}>Cancel</Button>
              <Button onClick={handleAddRecord} className="bg-teal-600 hover:bg-teal-700">
                Save Record
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reminder Settings Dialog */}
        <Dialog open={showSettings} onOpenChange={setShowSettings}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Reminder Settings</DialogTitle>
              <DialogDescription>Configure vaccination reminder notifications</DialogDescription>
            </DialogHeader>
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Email Reminders</Label>
                  <p className="text-xs text-gray-500">Receive email notifications for upcoming vaccines</p>
                </div>
                <Switch
                  checked={reminderSettings.email_reminders}
                  onCheckedChange={(checked) => setReminderSettings({ ...reminderSettings, email_reminders: checked })}
                />
              </div>
              
              {reminderSettings.email_reminders && (
                <>
                  <div>
                    <Label>Reminder Email</Label>
                    <Input
                      type="email"
                      value={reminderSettings.email}
                      onChange={(e) => setReminderSettings({ ...reminderSettings, email: e.target.value })}
                      placeholder="your@email.com"
                    />
                  </div>
                  <div>
                    <Label>Remind Before (days)</Label>
                    <Select 
                      value={String(reminderSettings.reminder_days_before)} 
                      onValueChange={(v) => setReminderSettings({ ...reminderSettings, reminder_days_before: parseInt(v) })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="3">3 days before</SelectItem>
                        <SelectItem value="7">7 days before</SelectItem>
                        <SelectItem value="14">14 days before</SelectItem>
                        <SelectItem value="30">30 days before</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSettings(false)}>Cancel</Button>
              <Button onClick={handleSaveSettings} className="bg-teal-600 hover:bg-teal-700">
                Save Settings
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default VaccinationTracker;
